import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  markBookedConversation,
  unmarkBookedConversation,
  type ConversationDetail,
} from '@/lib/api';
import { conversationQueryKey } from './useConversation';

/**
 * useMarkBooked — owner confirms the contact actually booked. Calls
 * POST /pwa/conversations/:id/mark-booked which tags the contact
 * `pwa_booked` (and clears `pwa_resolved` if present) so deriveStatus
 * flips the thread to `booked`.
 *
 * Since Square is no longer wired to GHL (2026-05-13), this is the
 * primary path to land a thread in booked status outside Shelby's
 * analyzer setting voice_ai_call_outcome='booked' during the call.
 */
export function useMarkBooked(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<{ booked: true }, Error, void>({
    mutationFn: () => markBookedConversation({ slug, conversationId }),
    onSuccess: () => {
      qc.setQueryData<ConversationDetail>(key, (curr) => {
        if (!curr) return curr;
        return { ...curr, status: 'booked' };
      });
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
    },
  });
}

/**
 * useUnmarkBooked — inverse. Removes the `pwa_booked` tag and lets
 * deriveStatus fall back to whatever the analyzer / quote window says.
 */
export function useUnmarkBooked(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<{ booked: false }, Error, void>({
    mutationFn: () => unmarkBookedConversation({ slug, conversationId }),
    onSuccess: () => {
      qc.setQueryData<ConversationDetail>(key, (curr) => {
        if (!curr) return curr;
        // Drop back to 'quoted' as a sensible default — the next refetch
        // reconciles against the contact's actual outcome.
        return { ...curr, status: curr.status === 'booked' ? 'quoted' : curr.status };
      });
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}
